class GeoPopup extends HTMLElement {
  constructor() {
    super();
    this.preferenceKey = 'ymn_geo_popup_locale';
    this.config = null;
    this.browserLocale = null;
    this.form = null;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.modal = this.querySelector('basic-modal');
    this.dropdown = this.querySelector('.geo-popup__dropdown');
    this.toggle = this.querySelector('[data-geo-popup-toggle]');
    this.list = this.querySelector('[data-geo-popup-list]');
    this.label = this.querySelector('[data-geo-popup-label]');
    this.form = this.querySelector('form');
    this.localeInput = this.querySelector('input[name="locale_code"]');
    this.textNodes = {
      title: this.querySelector('[data-geo-popup-text="title"]'),
      description: this.querySelector('[data-geo-popup-text="description"]'),
      confirm: this.querySelector('[data-geo-popup-text="confirm"]'),
      dismiss: this.querySelector('[data-geo-popup-text="dismiss"]'),
    };

    this.config = this.readConfig();
    this.browserLocale = this.detectBrowserLocale();

    if (!this.modal || !this.config || !this.shouldShow()) {
      this.remove();
      return;
    }

    this.applyLocalization();
    this.bindEvents();

    requestAnimationFrame(() => {
      this.modal.show();
    });
  }

  readConfig() {
    const node = this.querySelector('[data-geo-popup-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  normalizeLocale(code) {
    return String(code || '')
      .trim()
      .toLowerCase()
      .replace(/_/g, '-');
  }

  detectBrowserLocale() {
    const browserLocales = [];

    if (Array.isArray(navigator.languages) && navigator.languages.length) {
      browserLocales.push(...navigator.languages);
    } else if (navigator.language) {
      browserLocales.push(navigator.language);
    }

    const available = this.config?.languages || [];
    const availableMap = new Map(available.map((language) => [this.normalizeLocale(language.iso), language]));

    for (const rawLocale of browserLocales) {
      const normalized = this.normalizeLocale(rawLocale);
      if (!normalized) continue;

      if (availableMap.has(normalized)) {
        return availableMap.get(normalized);
      }

      const primary = normalized.split('-')[0];
      if (availableMap.has(primary)) {
        return availableMap.get(primary);
      }

      if (primary === 'pt') {
        const portuguese = available.find((language) => this.normalizeLocale(language.iso).startsWith('pt'));
        if (portuguese) return portuguese;
      }
    }

    return null;
  }

  shouldShow() {
    if (window.Shopify?.designMode) return false;
    if (localStorage.getItem(this.preferenceKey)) return false;
    if (!this.browserLocale) return false;

    const currentLocale = this.normalizeLocale(this.config.currentLocale);
    const browserLocale = this.normalizeLocale(this.browserLocale.iso);

    return currentLocale !== browserLocale;
  }

  rememberPreference(locale) {
    localStorage.setItem(this.preferenceKey, locale);
  }

  persistLocale(locale) {
    if (!this.form || !this.localeInput || this.isSubmitting) return;

    this.isSubmitting = true;
    this.localeInput.value = locale;
    this.form.submit();
  }

  saveCurrentLocale(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.rememberPreference(this.config.currentLocale);
    this.persistLocale(this.config.currentLocale);
  }

  applyLocalization() {
    const uiLocale = this.browserLocale.iso;
    const suggestedLocale = this.browserLocale.iso;
    const strings = this.config.translations[uiLocale] || this.config.translations[this.config.currentLocale];

    if (strings) {
      Object.entries(this.textNodes).forEach(([key, node]) => {
        if (node && strings[key]) {
          node.textContent = strings[key];
        }
      });
    }

    this.selectLocale(suggestedLocale, this.browserLocale.label);
  }

  selectLocale(locale, label) {
    if (this.localeInput) this.localeInput.value = locale;
    if (this.label && label) this.label.textContent = label;

    this.list?.querySelectorAll('.geo-popup__dropdown-option').forEach((item) => {
      const button = item.querySelector('[data-geo-popup-option]');
      const isSelected = button?.dataset.value === locale;
      item.classList.toggle('is-selected', isSelected);
    });
  }

  bindEvents() {
    this.toggle?.addEventListener('click', this.onToggleClick.bind(this));
    this.list?.querySelectorAll('[data-geo-popup-option]').forEach((button) => {
      button.addEventListener('click', this.onOptionClick.bind(this));
    });
    this.form?.addEventListener('submit', () => {
      this.rememberPreference(this.localeInput?.value || this.config.currentLocale);
    });
    this.querySelectorAll('[data-geo-popup-save-current]').forEach((element) => {
      element.addEventListener('click', this.saveCurrentLocale.bind(this));
    });
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  onToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropdown?.classList.toggle('is-open');
    this.toggle?.setAttribute('aria-expanded', this.dropdown?.classList.contains('is-open') ? 'true' : 'false');
  }

  onOptionClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    this.selectLocale(button.dataset.value, button.dataset.label);
    this.dropdown?.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  onDocumentClick(event) {
    if (!this.dropdown?.classList.contains('is-open')) return;
    if (this.dropdown.contains(event.target)) return;
    this.dropdown.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  onKeyUp(event) {
    if (event.code !== 'Escape' || !this.dropdown?.classList.contains('is-open')) return;
    this.dropdown.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('geo-popup', GeoPopup);
